import * as React from "react";
import { useEffect, useRef, useState } from "react";

import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { alpha, keyframes } from "@mui/material/styles";
import useTheme from "@mui/material/styles/useTheme";
import useMediaQuery from "@mui/material/useMediaQuery";

import { misc, neutral } from "app/themes/colors";

import { SHORT_SCREEN } from "./login-promo-sizing";
import {
  LoginPromoSource,
  trackLoginPromoSelected,
  trackLoginPromoShown
} from "./login-promo-telemetry";
import { LOGIN_PROMOS, LoginPromo } from "./login-promos";
import {
  LOGIN_PROMO_INTERVAL_MS,
  useLoginPromoRotation
} from "./use-login-promo-rotation.hook";

const fill = keyframes`
  from { transform: scaleX(0); }
  to { transform: scaleX(1); }
`;

function usePageHidden() {
  const [hidden, setHidden] = useState(
    () => typeof document !== "undefined" && document.hidden
  );
  useEffect(() => {
    const onChange = () => setHidden(document.hidden);
    document.addEventListener("visibilitychange", onChange);
    return () => document.removeEventListener("visibilitychange", onChange);
  }, []);
  return hidden;
}

function PromoSlide({ promo, active }: { promo: LoginPromo; active: boolean }) {
  const theme = useTheme();
  const white = theme.palette.common.white;
  const { accent, Artwork } = promo;

  return (
    <Stack
      role="group"
      aria-roledescription="slide"
      aria-label={promo.label}
      aria-hidden={!active}
      data-promo={promo.key}
      data-active={active}
      gap={{ md: 4, xl: 5 }}
      justifyContent="center"
      sx={{
        [SHORT_SCREEN]: { gap: 3 },
        // Every slide shares one grid cell, so the panel is as tall as the
        // tallest slide and the install card below never moves.
        gridArea: "1 / 1",
        opacity: active ? 1 : 0,
        visibility: active ? "visible" : "hidden",
        transform: active ? "none" : "translateY(6px)",
        // A fade, not a sideways slide: nothing sweeps across the screen
        // while someone is typing a password next to it.
        transition: active
          ? "opacity 0.6s ease, transform 0.6s ease, visibility 0s"
          : "opacity 0.6s ease, transform 0.6s ease, visibility 0s linear 0.6s",
        "@media (prefers-reduced-motion: reduce)": {
          transition: "none",
          transform: "none"
        }
      }}
    >
      <Stack gap={2}>
        {/* Two pills on one row where the panel is wide enough. The notice
            is longer than a label chip, so on a narrower window it wraps
            onto its own line rather than overflowing. */}
        <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1.25}>
          <Typography
            component="span"
            variant="caption"
            px={1.5}
            py={0.625}
            flexShrink={0}
            sx={{
              // Explicit px: a bare number here is scaled by
              // theme.shape.borderRadius, which is not what "pill" means.
              borderRadius: "999px",
              bgcolor: alpha(accent, 0.18),
              color: accent,
              fontWeight: 700,
              letterSpacing: 1,
              textTransform: "uppercase"
            }}
          >
            {promo.label}
          </Typography>
          <Stack
            component="span"
            direction="row"
            alignItems="center"
            gap={0.875}
            px={1.75}
            py={0.625}
            sx={{
              borderRadius: "999px",
              bgcolor: misc.darkYellow,
              color: neutral[900]
            }}
          >
            <InfoOutlinedIcon sx={{ fontSize: 15 }} />
            <Typography component="span" variant="caption" fontWeight={700}>
              {promo.notice}
            </Typography>
          </Stack>
        </Stack>
        <Typography
          component="h2"
          color={white}
          fontSize={{ md: 46, xl: 58 }}
          sx={{ [SHORT_SCREEN]: { fontSize: 40 } }}
          fontWeight={700}
          lineHeight={1.15}
          letterSpacing="-0.8px"
          aria-label={promo.headlineText}
        >
          {promo.headline}
        </Typography>
      </Stack>

      <Stack direction="row" alignItems="center" gap={4.5}>
        <Stack gap={2.75} flexGrow={1}>
          {promo.benefits.map(({ Icon, title, body }) => (
            <Stack
              key={title}
              direction="row"
              gap={1.75}
              alignItems="flex-start"
            >
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                flexShrink={0}
                width={{ md: 40, xl: 48 }}
                height={{ md: 40, xl: 48 }}
                borderRadius="10px"
                bgcolor={alpha(accent, 0.16)}
              >
                <Icon sx={{ fontSize: { md: 20, xl: 24 }, color: accent }} />
              </Box>
              <Stack gap={0.375} pt={0.25}>
                <Typography
                  variant="body2"
                  fontWeight={700}
                  color={white}
                  fontSize={{ xl: "1rem" }}
                >
                  {title}
                </Typography>
                <Typography
                  variant="body1"
                  color={alpha(white, 0.64)}
                  fontSize={{ xl: "1.125rem" }}
                  // body1's fixed 17px line height is too tight once xl
                  // enlarges the text and a benefit wraps to two lines.
                  lineHeight={{ xl: 1.4 }}
                >
                  {body}
                </Typography>
              </Stack>
            </Stack>
          ))}
        </Stack>
        <Artwork />
      </Stack>
    </Stack>
  );
}

interface Props {
  /** Rendered under the slides, inside the pause-on-hover area (the install card). */
  children?: React.ReactNode;
  promos?: LoginPromo[];
  intervalMs?: number;
}

/**
 * The login screen's right-hand panel: product promos that take turns, with
 * whatever `children` holds pinned underneath. See the folder's CLAUDE.md.
 */
export default function LoginPromoPanel({
  children,
  promos = LOGIN_PROMOS,
  intervalMs = LOGIN_PROMO_INTERVAL_MS
}: Props) {
  const theme = useTheme();
  const white = theme.palette.common.white;
  const reducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const pageHidden = usePageHidden();
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);

  const keys = React.useMemo(() => promos.map(({ key }) => key), [promos]);
  const { activeIndex, cycle, running, select } = useLoginPromoRotation({
    keys,
    paused: hovered || focused || pageHidden,
    autoRotate: !reducedMotion,
    intervalMs
  });

  // One RUM action per promo per page load; see login-promo-telemetry.ts.
  const reported = useRef(new Set<string>());
  const nextSource = useRef<LoginPromoSource>("start");
  const activeKey = promos[activeIndex]?.key;
  useEffect(() => {
    if (!activeKey || reported.current.has(activeKey)) return;
    reported.current.add(activeKey);
    trackLoginPromoShown(activeKey, nextSource.current);
  }, [activeKey]);
  useEffect(() => {
    nextSource.current = "rotation";
  }, [activeKey]);

  const onSelect = (index: number) => {
    nextSource.current = "click";
    trackLoginPromoSelected(promos[index].key);
    select(index);
  };

  return (
    <Stack
      gap={{ md: 3.5, xl: 4.5 }}
      sx={{ [SHORT_SCREEN]: { gap: 2.5 } }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setFocused(true)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
          setFocused(false);
        }
      }}
    >
      {/* Full-panel colour behind a promo that has its own. Absolutely
          positioned against the layout's right-hand Grid (the nearest
          positioned ancestor: keep this Stack and the layout's wrapper Box
          static), so it covers the padding too and fades with the slide.
          Content below is position: relative to paint above it. */}
      {promos.map(
        (promo, index) =>
          promo.background && (
            <Box
              key={`background-${promo.key}`}
              aria-hidden
              data-testid="login-promo-background"
              data-background-for={promo.key}
              data-visible={index === activeIndex}
              position="absolute"
              top={0}
              right={0}
              bottom={0}
              left={0}
              bgcolor={promo.background}
              sx={{
                opacity: index === activeIndex ? 1 : 0,
                transition: "opacity 0.6s ease",
                pointerEvents: "none",
                "@media (prefers-reduced-motion: reduce)": {
                  transition: "none"
                }
              }}
            />
          )
      )}

      <Box
        role="region"
        aria-roledescription="carousel"
        aria-label="Suno products"
        display="grid"
        position="relative"
      >
        {promos.map((promo, index) => (
          <PromoSlide
            key={promo.key}
            promo={promo}
            active={index === activeIndex}
          />
        ))}
      </Box>

      {promos.length > 1 && (
        <Stack
          direction="row"
          gap={1.75}
          position="relative"
          aria-label="Choose a product"
        >
          {promos.map((promo, index) => {
            const active = index === activeIndex;
            return (
              <ButtonBase
                key={promo.key}
                aria-current={active ? "true" : undefined}
                onClick={() => onSelect(index)}
                sx={{
                  flex: "0 0 160px",
                  flexDirection: "column",
                  alignItems: "stretch",
                  gap: 0.875,
                  py: 0.5,
                  borderRadius: "4px",
                  color: active ? white : alpha(white, 0.6),
                  "&:hover": { color: white },
                  "&.Mui-focusVisible": {
                    outline: `2px solid ${white}`,
                    outlineOffset: 4
                  }
                }}
              >
                <Typography
                  component="span"
                  variant="caption"
                  textAlign="left"
                  fontWeight={700}
                  letterSpacing={0.8}
                  sx={{ textTransform: "uppercase", color: "inherit" }}
                >
                  {promo.label}
                </Typography>
                <Box
                  component="span"
                  display="block"
                  height={4}
                  borderRadius="4px"
                  overflow="hidden"
                  bgcolor={alpha(white, 0.2)}
                >
                  {active && (
                    <Box
                      key={`${activeIndex}-${cycle}`}
                      component="span"
                      data-testid="login-promo-progress"
                      data-running={running}
                      display="block"
                      height="100%"
                      bgcolor={white}
                      sx={{
                        transformOrigin: "left center",
                        ...(reducedMotion
                          ? {}
                          : {
                              transform: "scaleX(0)",
                              animation: `${fill} ${intervalMs}ms linear forwards`,
                              animationPlayState: running ? "running" : "paused"
                            })
                      }}
                    />
                  )}
                </Box>
              </ButtonBase>
            );
          })}
        </Stack>
      )}

      <Box position="relative">{children}</Box>
    </Stack>
  );
}
